import { DatePipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import JSZip from 'jszip';
import { BehaviorSubject } from 'rxjs';
import { Flow } from '../obj/Flow';
import { FlowManagementService } from '../services/flow-management.service';

@Component({
    selector: 'app-zip',
    templateUrl: './zip.component.html',
    styleUrls: ['./zip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ZipComponent implements OnInit {

  loaded = false;
  all: Flow[] = [];
  filenametype: string = 'flow-display-name.json';
  public results = new BehaviorSubject([]);

  constructor(public flowManagementService: FlowManagementService, private datePipe: DatePipe) {
  }

  ngOnInit(): void {
    this.loaded = true;
  }

  async exportZip() {
    this.loaded = false;
    const environment = this.flowManagementService.currentEnvironment;
    this.results.next([`starting... "${environment}"`]);

    try {
      // Fetch the lightweight flow list (no definitions). Definitions come from
      // per-flow getFlow calls below — we don't cache them anymore.
      const flows = await this.flowManagementService.getFlows(
        environment, false, false, true, false, false, false, false, false
      );
      this.results.next([...this.results.value, `"${environment}" has ${flows.length} flows`]);
      this.all = flows;
      this.flowManagementService.serviceStatus$.next('');

      const zip = new JSZip();
      const re = /[\\~#%&*{}/:<>?|\"]/gi;

      flows.sort((a, b) => a.displayName.localeCompare(b.displayName));

      const names: string[] = [];
      const chunkSize = 4;
      for (let i = 0; i < flows.length; i += chunkSize) {
        const chunk = flows.slice(i, i + chunkSize);
        this.flowManagementService.serviceStatus$.next(`Fetching definitions ${Math.min(i + chunk.length, flows.length)} / ${flows.length}...`);
        const fetched = await Promise.all(chunk.map((flow) => this.flowManagementService
          .getFlow(flow.environment, flow.flowName, false)
          .toPromise()
          .then((detail) => ({ flow, definition: detail?.properties?.definition || null }))
          .catch((reason) => {
            console.warn('[zip] definition fetch failed', flow.flowName, reason);
            return { flow, definition: null };
          })
        ));

        for (const { flow, definition } of fetched) {
          let filename = '';
          switch (this.filenametype) {
            case 'flow-display-name.json':
              filename = `${flow.displayName}.json`;
              break;
            case 'flow-guid-name.json':
              filename = `${flow.flowName}.json`;
              break;
            case 'flow-guid-display-name.json':
              filename = `${flow.flowName}-${flow.displayName}.json`;
              break;
          }
          filename = filename.replace(re, '_');

          if (this.filenametype == 'flow-display-name.json') {
            if (names.indexOf(filename) !== -1) {
              this.results.next([...this.results.value, `"${filename}" already exists, disambiguating`]);
              filename = `${flow.displayName}-${flow.flowName}.json`;
            }
            names.push(filename);
          }

          if (!definition) {
            this.results.next([...this.results.value, `"${flow.displayName}" (${flow.flowName}) — no definition available, skipping`]);
            continue;
          }

          this.results.next([...this.results.value, `"${flow.displayName}" (${flow.flowName}) written to ${filename}`]);
          zip.file(filename, JSON.stringify(definition, null, 2), { binary: true });
        }
      }

      const contents = await zip.generateAsync({ type: 'blob' });
      this.results.next([...this.results.value, `Exporting to zip file...`]);
      const link: any = window.URL.createObjectURL(contents);
      const fileLink = document.createElement('a');
      fileLink.href = link;
      fileLink.setAttribute('download', `${environment}-flows-${this.datePipe.transform(Date.now(), 'yyyy-MM-dd')}.zip`);
      fileLink.click();
    } finally {
      this.loaded = true;
      this.flowManagementService.serviceStatus$.next('');
    }

    return false;
  }

}
